import { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tabs,
  Tab,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';

interface EnvConfig {
  key: string;
  displayName: string;
  color: string;
  branch: string;
}

interface Pipeline {
  id: number;
  status: string;
  ref: string;
  sha: string;
  web_url: string;
  created_at: string;
  updated_at: string;
  source: string;
}

interface CommitInfo {
  title: string;
  author_name: string;
}

const useStyles = makeStyles(theme => ({
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: 40,
    '& .MuiTab-root': {
      minHeight: 40,
      fontWeight: 700,
      textTransform: 'uppercase',
      fontSize: '0.75rem',
      letterSpacing: '0.06em',
    },
  },
  table: {
    '& th': {
      fontWeight: 600,
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: theme.palette.text.secondary,
      borderBottom: `2px solid ${theme.palette.divider}`,
      padding: theme.spacing(1, 2),
    },
    '& td': {
      padding: theme.spacing(1.2, 2),
    },
  },
  commitHash: {
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    fontWeight: 600,
  },
  commitMsg: {
    fontSize: '0.8rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 350,
  },
  commitAuthor: {
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
  },
  pipelineId: {
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
  },
  link: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 3,
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' },
  },
  date: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  empty: {
    textAlign: 'center',
    padding: theme.spacing(4),
  },
}));

function getStatusColor(status: string): string {
  switch (status) {
    case 'success': return '#4caf50';
    case 'failed': return '#f44336';
    case 'running': return '#2196f3';
    case 'pending': return '#ff9800';
    case 'canceled': return '#9e9e9e';
    case 'skipped': return '#9e9e9e';
    default: return '#9e9e9e';
  }
}

function getStatusLabel(status: string): string {
  switch (status) {
    case 'success': return 'Passed';
    case 'failed': return 'Failed';
    case 'running': return 'Running';
    case 'pending': return 'Pending';
    case 'canceled': return 'Canceled';
    case 'skipped': return 'Skipped';
    default: return status;
  }
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `il y a ${mins}min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `il y a ${days}j`;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export const BuildContent = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const backendBaseUrl = configApi.getString('backend.baseUrl');

  const annotations = entity.metadata.annotations ?? {};
  const gitlabInstance = annotations['gitlab.com/instance'] ?? 'gitlab.com';
  const projectSlug = annotations['gitlab.com/project-slug'];
  const projectId = annotations['gitlab.com/project-id'];

  const [activeTab, setActiveTab] = useState(0);
  const [envs, setEnvs] = useState<EnvConfig[]>([]);
  const [pipelines, setPipelines] = useState<Record<string, Pipeline[]>>({});
  const [commits, setCommits] = useState<Record<string, CommitInfo>>({});
  const [loading, setLoading] = useState<Record<string, boolean>>({});

  // Read environments from config
  useEffect(() => {
    try {
      const arr = configApi.getConfigArray('fondative.environments');
      setEnvs(
        arr.map(c => ({
          key: c.getString('key'),
          displayName: c.getString('displayName'),
          color: c.getOptionalString('color') ?? '#1976d2',
          branch: c.getOptionalString('branch') ?? 'main',
        })),
      );
    } catch {
      setEnvs([]);
    }
  }, [configApi]);

  // Resolve branch per env: entity annotation overrides global config
  // Annotation format: fondative.io/branch-{envKey} (e.g. fondative.io/branch-dev)
  const getBranch = (env: EnvConfig) =>
    annotations[`fondative.io/branch-${env.key}`] ?? env.branch;

  // Fetch pipelines per env branch
  useEffect(() => {
    if (!envs.length || !projectId) return;
    envs.forEach(env => {
      const branch = getBranch(env);
      setLoading(prev => ({ ...prev, [env.key]: true }));
      fetchApi
        .fetch(
          `${backendBaseUrl}/api/gitlab/rest/${gitlabInstance}/projects/${projectId}/pipelines?ref=${branch}&per_page=15`,
        )
        .then(res => (res.ok ? res.json() : Promise.reject()))
        .then((data: Pipeline[]) => {
          setPipelines(prev => ({ ...prev, [env.key]: data }));
          setLoading(prev => ({ ...prev, [env.key]: false }));
          // Fetch commit messages for unique SHAs
          const uniqueShas = [...new Set(data.map(p => p.sha))];
          uniqueShas.forEach(sha => {
            if (!sha) return;
            fetchApi
              .fetch(
                `${backendBaseUrl}/api/gitlab/rest/${gitlabInstance}/projects/${projectId}/repository/commits/${sha}`,
              )
              .then(r => (r.ok ? r.json() : null))
              .then(c => {
                if (c) {
                  setCommits(prev => ({
                    ...prev,
                    [sha]: { title: c.title, author_name: c.author_name },
                  }));
                }
              })
              .catch(() => {});
          });
        })
        .catch(() => {
          setPipelines(prev => ({ ...prev, [env.key]: [] }));
          setLoading(prev => ({ ...prev, [env.key]: false }));
        });
    });
  }, [envs, projectId, gitlabInstance, backendBaseUrl, fetchApi]);

  if (!projectId && !projectSlug) {
    return (
      <Box p={3}>
        <Typography color="textSecondary">
          Ajoutez l'annotation <code>gitlab.com/project-id</code> pour activer la vue Build.
        </Typography>
      </Box>
    );
  }

  if (!envs.length) return null;

  const currentEnv = envs[activeTab];
  const currentPipelines = pipelines[currentEnv.key] ?? [];
  const isLoading = loading[currentEnv.key] ?? true;

  return (
    <Card>
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        className={classes.tabs}
        indicatorColor="primary"
        textColor="primary"
      >
        {envs.map(env => (
          <Tab
            key={env.key}
            label={`${env.displayName} (${getBranch(env)})`}
            style={{ color: env.color }}
          />
        ))}
      </Tabs>
      <CardContent style={{ padding: 0 }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : currentPipelines.length === 0 ? (
          <Box className={classes.empty}>
            <Typography color="textSecondary">
              Aucun pipeline sur la branche <strong>{currentEnv.branch}</strong>
            </Typography>
          </Box>
        ) : (
          <Table className={classes.table} size="small">
            <TableHead>
              <TableRow>
                <TableCell>Pipeline</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                    Commit
                    <Tooltip title="Commit ID (8 premiers caractères)" arrow>
                      <InfoOutlinedIcon style={{ fontSize: 14, color: '#9e9e9e' }} />
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentPipelines.map(p => (
                <TableRow key={p.id} hover>
                  <TableCell>
                    <a
                      href={p.web_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.link}
                    >
                      <span className={classes.pipelineId}>#{p.id}</span>
                      <OpenInNewIcon style={{ fontSize: 12 }} />
                    </a>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={getStatusLabel(p.status)}
                      style={{
                        backgroundColor: getStatusColor(p.status),
                        color: '#fff',
                        fontSize: '0.65rem',
                        height: 20,
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography className={classes.commitHash}>
                      {p.sha.substring(0, 8)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {commits[p.sha] ? (
                      <Box>
                        <Tooltip title={commits[p.sha].title} arrow>
                          <Typography className={classes.commitMsg}>
                            {commits[p.sha].title}
                          </Typography>
                        </Tooltip>
                        <Typography className={classes.commitAuthor}>
                          {commits[p.sha].author_name}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="textSecondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography className={classes.date}>
                      {formatDate(p.created_at)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};
